import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import {
  Assessment,
  DropdownField,
  Field,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  FormElement,
  getAssessmentName,
  getPatientFileField,
  getPatientFilesField,
  getTempConsentFormStoragePath,
  objectToArray,
  OnUploadProgress,
  WithId,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'

import { useFirebase } from '@chem-po/firebase-web'
import { PopUpMessageContext, useApp } from '@hb/react'

import { useCallback, useContext, useMemo } from 'react'
import { useNavigate } from '../../hooks/navigation'
import { processFormElementData } from '../../backend/db/utils/processData'
import { processPatientFileUpload } from '../../backend/functions/other'
import { useMyAssessments } from '../../contexts'
import { uploadSignedConsentForm, useConsentFormsStatus } from '../../hooks'
import { SimpleForm } from '../forms/FinalForm/SimpleForm'
import { DefaultModal } from '../Modals/DefaultModal'

interface UploadFileModalProps {
  isOpen: boolean
  onClose: () => void
  assessmentsArr: Array<WithId<Assessment>>
}

export const UploadPatientFileModal = ({
  isOpen,
  onClose,
  assessmentsArr,
}: UploadFileModalProps) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const { auth } = useFirebase()
  const { appName } = useApp()

  const { user: userData, userId } = useMyAssessments()
  const { user } = userData ?? {}
  const { consentForms } = useConsentFormsStatus(userId, user, null)
  const unsignedConsentForms = useMemo(
    () => objectToArray(consentForms ?? {}).filter(form => !form.signedOn),
    [consentForms],
  )
  const navigate = useNavigate()

  const consentFormFields = useMemo<Record<string, Field> | null>(
    () =>
      unsignedConsentForms.length
        ? {
            isConsentForm: {
              type: FieldTypes.BOOLEAN,
              placeholder: 'Are you uploading a signed consent form?',
            },
            consentFormId: {
              type: FieldTypes.DROPDOWN,
              placeholder: 'Select the consent form you are uploading',
              options: unsignedConsentForms.map(form => ({
                text: form.name,
                id: form.id,
              })),
              condition: v => !!v?.isConsentForm,
            },
          }
        : null,
    [unsignedConsentForms],
  )

  const assessmentField = useMemo<DropdownField | null>(
    () =>
      assessmentsArr.length
        ? {
            type: FieldTypes.DROPDOWN,
            placeholder: 'Are these files associated with a pregnancy?',
            options: [
              ...assessmentsArr.map(assessment => ({
                text: getAssessmentName(assessment),
                id: assessment.id,
              })),
              {
                text: 'No - Save to my patient profile',
                id: 'NONE',
              },
            ],
            optional: true,
            condition: consentFormFields ? v => v?.isConsentForm === false : undefined,
          }
        : null,
    [assessmentsArr, consentFormFields],
  )

  const newFileId = useMemo(() => Date.now(), [])
  const filesField = useMemo(() => getPatientFilesField(`${newFileId}`), [newFileId])
  const consentFormFileField = useMemo(
    () => getPatientFileField(`${newFileId}`, 'pdf'),
    [newFileId],
  )

  const field = useMemo<FieldMap>(() => {
    const children: Record<string, FormElement> = consentFormFields ? { ...consentFormFields } : {}
    if (assessmentField) {
      children.pregnancyId = assessmentField
    }
    children.files = {
      ...filesField,
      condition: consentFormFields ? v => v?.isConsentForm === false : undefined,
    }
    if (consentFormFileField) {
      children.consentFormFile = {
        ...consentFormFileField,
        condition: v => v?.isConsentForm === true,
      }
    }
    return {
      name: 'Patient File',
      children,
    }
  }, [assessmentField, filesField, consentFormFields, consentFormFileField])

  const handleSubmit = useCallback(
    async (data: FieldMapValue, onUploadProgress: OnUploadProgress) => {
      const uid = auth?.currentUser?.uid
      if (!uid) return { error: 'Not logged in' }
      if (!newFileId) return processResponse({ error: 'Internal error' })

      const { isConsentForm, consentFormFile, consentFormId } = data
      const pregnancyId = data.pregnancyId === 'NONE' ? null : (data.pregnancyId ?? null)
      const getStoragePath = () => {
        if (isConsentForm) {
          return getTempConsentFormStoragePath(uid, false, consentFormId, Date.now())
        }
        // Upload to temp storage path
        return `temp-files/${uid}`
      }
      try {
        // Upload files to temp storage path
        const uploaded = await processFormElementData(
          appName,
          false,
          uid,
          getStoragePath(),
          isConsentForm ? consentFormFileField : filesField,
          isConsentForm ? consentFormFile : data.files,
          undefined,
          onUploadProgress,
        )
        if (!uploaded) return processResponse({ error: 'Error uploading file' })

        if (isConsentForm) {
          if (typeof uploaded.storagePath !== 'string')
            return processResponse({ error: 'Error uploading file' })
          await uploadSignedConsentForm({
            consentFormId,
            storagePath: uploaded.storagePath,
          })
        } else {
          // Call Firebase function to process the upload
          const processedFiles = Array.isArray(uploaded) ? uploaded : [uploaded]
          await processPatientFileUpload({
            userId: uid,
            files: processedFiles,
            pregnancyId: pregnancyId || null,
            appName,
          })
        }

        onUploadProgress({})
        onClose()
        if (isConsentForm) {
          navigate('/consent-forms')
        }
        return processResponse({ success: 'Files uploaded successfully' })
      } catch (err: any) {
        console.error(err)
        return processResponse({
          error: err?.message || 'Error uploading file',
        })
      }
    },
    [
      processResponse,
      newFileId,
      appName,
      filesField,
      auth,
      onClose,
      consentFormFileField,
      navigate,
    ],
  )

  const initValue = useMemo(
    () => ({
      files: [],
      combineIntoPdf: true,
      fileName: '',
    }),
    [],
  )

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      overlayHeader
      size="lg"
      render={() => (
        <Box>
          <VStack spacing={0} align="stretch">
            <Flex px={4} w="100%" borderBottom="1px solid #cdcdcd" py={2} bg="gray.100">
              <Text fontFamily="Hero-New" fontSize="lg" fontWeight={500}>
                Upload File
              </Text>
            </Flex>
            <Box bg="gray.50" py={2}>
              <SimpleForm
                field={field}
                value={initValue}
                boxProps={{
                  bg: 'transparent',
                  py: 0,
                  px: 0,
                  boxShadow: 'none',
                }}
                onSubmit={(val, onUploadProgress) =>
                  handleSubmit(val, onUploadProgress)
                    .then(res => (res.error ? { [FORM_ERROR]: res.error } : undefined))
                    .catch(err =>
                      processResponse({
                        error: err?.message || 'An error occurred',
                      }),
                    )
                }
              />
            </Box>
          </VStack>
        </Box>
      )}
    />
  )
}
